import { Button, cn } from '@playerbase/ui';
import type { DiscordGuildChannel } from '@playerbase/types';

import { FormSubmissionCard } from './form-submission-card.js';
import { buildActiveAt, type BuildChange } from '../games/build-timeline.js';
import { useShell } from '../shell/use-shell.js';
import { BuildChangedRow } from './build-changed-row.js';
import { interleaveBuildMarkers } from './interleave-build-markers.js';
import type { FormSubmissionEntry } from './use-feedback-inbox.js';

// ---------------------------------------------------------------------------
// Form submissions pane.
//
// Companion to the polls feed in the right-hand tab strip. The chip-
// level channel filter applies here client-side: a submission lives in
// channel #foo iff its form's surveyChannelId matches the chip's
// channelId. The list is server-paginated by the inbox hook (50 rows
// per page); "Show more" fetches the next page rather than expanding
// a local visible window.
// ---------------------------------------------------------------------------

interface FormSubmissionsFeedProps {
  entries: FormSubmissionEntry[];
  // Map keyed by channelId so the card can resolve a friendly channel
  // label without re-fetching guild channels.
  channelById: Map<string, DiscordGuildChannel>;
  // Page-level channel filter selection. null = "All channels".
  activeChannelId: string | null;
  // Build-change divider rows, interleaved into the submission list by
  // submitted_at. The same game-wide set the message + poll feeds get.
  buildChanges?: BuildChange[];
  // Highlight integration with the inbox container - same shape as the
  // polls feed.
  highlightSubmissionId?: string | null;
  registerRowRef?: (rowId: string, el: HTMLDivElement | null) => void;
  // Server-side pagination, owned by useFeedbackInbox. The feed renders
  // every entry the hook has loaded; the button asks for the next page.
  hasMoreFromServer: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}

function entryMatchesChannel(
  entry: FormSubmissionEntry,
  channelId: string | null,
): boolean {
  if (channelId === null) return true;
  return entry.surveyChannelId === channelId;
}

export function FormSubmissionsFeed({
  entries,
  channelById,
  activeChannelId,
  buildChanges = [],
  highlightSubmissionId,
  registerRowRef,
  hasMoreFromServer,
  loadingMore,
  onLoadMore,
}: FormSubmissionsFeedProps): JSX.Element {
  const { buildMarkers, devStages } = useShell();
  const filtered = entries.filter((e) => entryMatchesChannel(e, activeChannelId));

  return (
    <div
      key={activeChannelId ?? 'all'}
      className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150"
    >
      {filtered.length === 0 ? (
        <p className="px-4 py-6 text-text-muted text-sm">
          No form submissions yet. Forms you create in Feedback Settings will
          collect responses here.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {interleaveBuildMarkers(filtered, buildChanges, (e) => e.submittedAt).map((row) => {
            if (row.kind === 'build') {
              return (
                <li key={`build:${row.change.marker.id}`}>
                  <BuildChangedRow marker={row.change.marker} />
                </li>
              );
            }
            const entry = row.item;
            const channel =
              entry.surveyChannelId === null
                ? null
                : channelById.get(entry.surveyChannelId) ?? null;
            const channelLabel = channel?.name ?? null;
            return (
              <li key={entry.id}>
                <FormSubmissionCard
                  entry={entry}
                  channelLabel={channelLabel}
                  buildMarker={buildActiveAt(buildMarkers, devStages, entry.submittedAt)}
                  highlighted={highlightSubmissionId === entry.id}
                  innerRef={
                    registerRowRef === undefined
                      ? undefined
                      : (el) => registerRowRef(entry.id, el)
                  }
                />
              </li>
            );
          })}
        </ul>
      )}
      {hasMoreFromServer && (
        <div className={cn('flex justify-center border-t border-border px-4 py-3')}>
          <Button
            variant="ghost"
            size="sm"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Show more'}
          </Button>
        </div>
      )}
    </div>
  );
}
